import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { useAuth, DISPLAY_MODES } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Globe, DollarSign, Target, Phone, Monitor, Eye } from "lucide-react";
import { CURRENCIES } from "@/lib/currencyUtils";
import { cn } from "@/lib/utils";
import BillingSettingsCard from "@/components/BillingSettingsCard";
import usePageTitle from "@/hooks/usePageTitle";

const LANGUAGES = [
  { code: "en", name: "English", nativeName: "English" },
  { code: "es", name: "Spanish", nativeName: "Español" },
  { code: "fr", name: "French", nativeName: "Français" },
  { code: "ar", name: "Arabic", nativeName: "العربية" }
];

const SettingsPage = ({ settings, setSettings }) => {
  usePageTitle("pageTitles.settings");
  const { t, i18n } = useTranslation();
  const { displayMode, toggleDisplayMode } = useAuth();
  const [formData, setFormData] = useState({
    language: "en",
    currency: "USD",
    target_margin: 70.0,
    phone: "",
    restaurant_name: "",
    profile_logo_url: "",
    profile_logo_base64: "",
    display_mode: "standard"
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    if (settings) {
      setFormData({
        language: settings.language || "en",
        currency: settings.currency || "USD",
        target_margin: settings.target_margin || 70.0,
        phone: settings.phone || "",
        restaurant_name: settings.restaurant_name || "",
        profile_logo_url: settings.profile_logo_url || "",
        profile_logo_base64: settings.profile_logo_base64 || "",
        display_mode: settings.display_mode || displayMode || "standard"
      });
      // Set logo preview from base64 or URL
      if (settings.profile_logo_base64) {
        setLogoPreview(settings.profile_logo_base64);
      } else if (settings.profile_logo_url) {
        setLogoPreview(`${API}${settings.profile_logo_url}`);
      }
    }
  }, [settings, displayMode]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(t('settings.invalidImageType'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('settings.imageTooLarge'));
      return;
    }

    setUploading(true);
    try {
      // Convert image to Base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setFormData(prev => ({ 
          ...prev, 
          profile_logo_base64: base64String,
          profile_logo_url: "" // Clear URL if using base64
        }));
        setLogoPreview(base64String);
        toast.success(t('settings.logoUploaded'));
        setUploading(false);
      };
      reader.onerror = () => {
        toast.error(t('settings.uploadError'));
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error(t('settings.uploadError'));
      setUploading(false);
    }
  };

  const handleDeleteLogo = () => {
    setFormData(prev => ({ 
      ...prev, 
      profile_logo_base64: "",
      profile_logo_url: ""
    }));
    setLogoPreview(null);
    toast.success("Logo deleted successfully");
  };

  const handleDisplayModeChange = (mode) => {
    setFormData(prev => ({ ...prev, display_mode: mode }));
    // Update context immediately for UI preview
    if (mode !== displayMode) {
      toggleDisplayMode();
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLanguageChange = (value) => {
    handleInputChange("language", value);
    i18n.changeLanguage(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.put(`${API}/settings`, formData);
      setSettings(response.data);
      toast.success(t("settings.settingsUpdated"));
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error(t("settings.settingsFailed"));
    } finally {
      setLoading(false);
    }
  };

  // Get list of currencies sorted by name
  const currenciesList = Object.entries(CURRENCIES)
    .sort((a, b) => a[1].name.localeCompare(b[1].name))
    .map(([code, info]) => ({
      code,
      name: info.name,
      symbol: info.symbol
    }));

  return (
    <div className="min-h-screen bg-slate-950" data-testid="settings-page">
      <Toaster position="top-right" />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white font-manrope">
            {t("settings.title")}
          </h1>
          <p className="text-slate-400 mt-2">{t("settings.subtitle")}</p>
        </div>

        {/* 🆕 Billing & Subscription (deep-linked from ?tab=billing) */}
        <div className="mb-8">
          <BillingSettingsCard />
        </div>

        {/* Settings Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Restaurant Name & Branding */}
          <div className="bg-slate-900/50 rounded-lg border border-slate-800 p-6">
            <h2 className="text-xl font-semibold text-white mb-6">{t("settings.brandingSection")}</h2>
            
            {/* Restaurant Name */}
            <div className="mb-6">
              <Label htmlFor="restaurant_name" className="text-lg font-semibold text-white mb-1 block">
                {t("settings.restaurantName")}
              </Label>
              <p className="text-sm text-slate-400 mb-4">{t("settings.restaurantNameDesc")}</p>
              <Input
                id="restaurant_name"
                type="text"
                value={formData.restaurant_name}
                onChange={(e) => handleInputChange("restaurant_name", e.target.value)}
                placeholder={t("settings.restaurantNamePlaceholder")}
                className="bg-slate-800 border-slate-700 text-white w-full max-w-md"
                data-testid="restaurant-name-input"
              />
            </div>

            {/* Profile Logo */}
            <div>
              <Label className="text-lg font-semibold text-white mb-1 block">
                {t("settings.profileLogo")}
              </Label>
              <p className="text-sm text-slate-400 mb-4">{t("settings.logoDesc")}</p>
              <div className="flex items-center gap-4">
                {logoPreview && (
                  <div className="w-24 h-24 rounded-lg overflow-hidden border-2 border-amber-500/50 bg-slate-800">
                    <img src={logoPreview} alt="Profile Logo" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <label className="cursor-pointer">
                    <div className="px-4 py-2 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-400 rounded-lg transition-colors inline-block">
                      {uploading ? t('settings.uploading') : t('settings.uploadLogo')}
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                  {logoPreview && (
                    <button
                      type="button"
                      onClick={handleDeleteLogo}
                      className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 rounded-lg transition-colors"
                    >
                      Delete Logo
                    </button>
                  )}
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {t('settings.logoHint')} (Recommended: 512x512px for best results)
              </p>
            </div>
          </div>

          {/* Display Mode Setting - Tactical UI Toggle */}
          <div className="bg-slate-900/50 rounded-lg border border-amber-500/30 p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-500/10 rounded-lg">
                <Eye className="h-6 w-6 text-amber-500" />
              </div>
              <div className="flex-1">
                <Label className="text-lg font-semibold text-amber-500 mb-1 block">
                  🎯 Tactical UI Mode
                </Label>
                <p className="text-sm text-slate-400 mb-4">
                  High-visibility interface designed for kitchen environments. 
                  Larger text (18-20px), bigger buttons (48px), and enhanced touch targets for 'kitchen fingers.'
                </p>
                
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => handleDisplayModeChange(DISPLAY_MODES.STANDARD)}
                    className={cn(
                      "px-6 py-3 rounded-lg font-semibold transition-all",
                      formData.display_mode === DISPLAY_MODES.STANDARD
                        ? "bg-amber-500 text-slate-950"
                        : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                    )}
                  >
                    <Monitor className="inline-block h-5 w-5 mr-2" />
                    Standard
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => handleDisplayModeChange(DISPLAY_MODES.TACTICAL)}
                    className={cn(
                      "px-6 py-3 rounded-lg font-semibold transition-all",
                      formData.display_mode === DISPLAY_MODES.TACTICAL
                        ? "bg-amber-500 text-slate-950"
                        : "bg-slate-700 text-slate-300 hover:bg-slate-600"
                    )}
                  >
                    <Eye className="inline-block h-5 w-5 mr-2" />
                    Tactical (Kitchen Mode)
                  </button>
                </div>
                
                <div className="mt-4 p-4 bg-slate-800/50 rounded-lg">
                  <p className="text-sm text-slate-300">
                    <strong>Current Mode:</strong> <span className="text-amber-500 font-semibold">{formData.display_mode === DISPLAY_MODES.TACTICAL ? 'Tactical' : 'Standard'}</span>
                  </p>
                  <p className="text-xs text-slate-500 mt-2">
                    {formData.display_mode === DISPLAY_MODES.TACTICAL 
                      ? '✓ Large fonts, big buttons, optimized for kitchen environment'
                      : 'Standard web interface with default sizing'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Language Setting */}
          <div className="bg-slate-900/50 rounded-lg border border-slate-800 p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-500/10 rounded-lg">
                <Globe className="h-6 w-6 text-amber-500" />
              </div>
              <div className="flex-1">
                <Label htmlFor="language" className="text-lg font-semibold text-white mb-1 block">
                  {t("settings.language")}
                </Label>
                <p className="text-sm text-slate-400 mb-4">{t("settings.languageDesc")}</p>
                <Select value={formData.language} onValueChange={handleLanguageChange}>
                  <SelectTrigger 
                    className="bg-slate-800 border-slate-700 text-white w-full max-w-md" 
                    data-testid="language-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {LANGUAGES.map((lang) => (
                      <SelectItem key={lang.code} value={lang.code} className="text-white">
                        {lang.nativeName} ({lang.name})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Currency Setting */}
          <div className="bg-slate-900/50 rounded-lg border border-slate-800 p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-500/10 rounded-lg">
                <DollarSign className="h-6 w-6 text-amber-500" />
              </div>
              <div className="flex-1">
                <Label htmlFor="currency" className="text-lg font-semibold text-white mb-1 block">
                  {t("settings.currency")}
                </Label>
                <p className="text-sm text-slate-400 mb-4">{t("settings.currencyDesc")}</p>
                <Select value={formData.currency} onValueChange={(value) => handleInputChange("currency", value)}>
                  <SelectTrigger 
                    className="bg-slate-800 border-slate-700 text-white w-full max-w-md" 
                    data-testid="currency-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700 max-h-[400px]">
                    {currenciesList.map((curr) => (
                      <SelectItem key={curr.code} value={curr.code} className="text-white">
                        {curr.symbol} {curr.code} - {curr.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Target Margin Setting */}
          <div className="bg-slate-900/50 rounded-lg border border-slate-800 p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-500/10 rounded-lg">
                <Target className="h-6 w-6 text-amber-500" />
              </div>
              <div className="flex-1">
                <Label htmlFor="target_margin" className="text-lg font-semibold text-white mb-1 block">
                  {t("settings.targetMargin")}
                </Label>
                <p className="text-sm text-slate-400 mb-4">{t("settings.targetMarginDesc")}</p>
                <div className="flex items-center gap-3 max-w-md">
                  <Input
                    id="target_margin"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={formData.target_margin}
                    onChange={(e) => handleInputChange("target_margin", parseFloat(e.target.value))}
                    className="bg-slate-800 border-slate-700 text-white font-mono flex-1"
                    data-testid="target-margin-input"
                  />
                  <span className="text-slate-300 font-mono text-lg">%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Phone Number Setting */}
          <div className="bg-slate-900/50 rounded-lg border border-slate-800 p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-500/10 rounded-lg">
                <Phone className="h-6 w-6 text-amber-500" />
              </div>
              <div className="flex-1">
                <Label htmlFor="phone" className="text-lg font-semibold text-white mb-1 block">
                  {t("settings.phone")}
                </Label>
                <p className="text-sm text-slate-400 mb-4">{t("settings.phoneDesc")}</p>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  placeholder={t("settings.phonePlaceholder")}
                  className="bg-slate-800 border-slate-700 text-white w-full max-w-md"
                  data-testid="phone-input"
                />
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              type="submit"
              className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold px-8"
              disabled={loading}
              data-testid="save-settings-button"
            >
              {loading ? t("ingredientModal.saving") : t("settings.saveSettings")}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default SettingsPage;
